import React, { FC, useCallback, useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { DataTable } from 'src/common/components/table/data-table';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import { useCurrentOrganization } from 'src/store/organization';
import { apiGetAllSessions } from 'src/utils/journeyApi';
import { useDepsMatchingCallbackRef } from 'src/utils/react/deps-matching-callback-ref.hook';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { Pagination } from 'src/common/Pagination';

import {
  TableSortableColumnHeader,
  TableSortableColumnHeaderOrderType,
  TableSortableColumnHeaderSortType,
} from 'src/common/components/table/sortable-column-header';
import { formatDateTime } from 'src/utils/date';
import { Helmet } from 'react-helmet';
import { Nullable } from 'src/types/nullable.type';
import isNull from 'lodash/isNull';
import { SessionHoverableCardSmall } from './components/hoverable-content-card';
import { useCurrentUser } from 'src/store/user';
import { secondsToHms } from 'src/utils/time';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { isJourneyAccessible } from 'src/utils/journey-access';
import { useJourneys } from 'src/store/journeys';

type DashboardInsightsContentProps = {};

type DashboardInsightsContentListHeaderTypes = 'total_views' | 'duration' | 'average_time' | 'name' | 'session_uuid' | 'journey_name' | 'journey_url' | 'created_at';

type DashboardInsightsContentListHeaderSortTypes =
  TableSortableColumnHeaderSortType<DashboardInsightsContentListHeaderTypes>;

export const DashboardSessionsContentAll: FC<DashboardInsightsContentProps> = () => {
  const history = useHistory();
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState<Nullable<boolean>>(null);
  const [pages, setPages] = useState(1);

  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const currentUser = useCurrentUser((state)=>state.currentUser);
  const { subscriptionPlanType, isOnTrial } = useCurrentSubscription();
  const { showUpgradeModal } = useFeatureRestrictions();
  const journeys = useJourneys((state) => state.journeys);

  const {
    filterBarParams: { search_term, page, sort_by, sort_order },
    updatePage,
    updateSort,
  } = useFilterBarParams({
    sort_by: 'total_views',
  });

  const onFetchContentsInsightsRef = useDepsMatchingCallbackRef(
    (response: any) => {
      setSessions(response.session_replays);
      setPages(response.pages);
      setLoading(false);
    },
    [currentOrganization.id, search_term, page]
  );

  const fetchSessionReplays = useCallback(async (organizationId) => {
    const params = {
      page: page,
      ...(search_term && { search_str: search_term }),
      ...(sort_by && { sort_by: sort_by }),
      ...(sort_order && { sort_order: sort_order }),
    };

    try {
      setLoading(true);
      const response = await apiGetAllSessions(organizationId,params);
      onFetchContentsInsightsRef.current &&
        onFetchContentsInsightsRef.current(response, [currentOrganization.id, search_term, page]);
    } catch (e) {
      console.error(e);
    }
  }, [onFetchContentsInsightsRef, currentOrganization.id, search_term, page, sort_by, sort_order]);

  useEffect(() => {
    if(currentUser){
      fetchSessionReplays(currentUser.last_organization.id)
    }
  }, [fetchSessionReplays,currentUser]);

  const onTableHeaderClick = useCallback(
    (sortStack: DashboardInsightsContentListHeaderSortTypes) => {
      updateSort(sortStack.sortName, sortStack.orderBy);
    },
    [updateSort]
  );

  const columns = useMemo(
    () => [
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={false}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='session_uuid'
            onClick={()=>{}}
            title='Session Id'
            tooltip='Session ID'
          />
        ),
        accessor: 'session_uuid',
        width: 462,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return <SessionHoverableCardSmall uuid={row.original.session_uuid} />;
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'duration'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='duration'
            onClick={onTableHeaderClick}
            title='Duration'
            tooltip='The total duration of this session in (hh:mm:ss) format'
          />
        ),
        accessor: 'duration',
        width: 180,
        allowShrinking: false,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return row.original.duration ? secondsToHms(row.original.duration) : '';
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'created_at'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='created_at'
            onClick={onTableHeaderClick}
            title='Created'
            tooltip='When this session replay was created'
          />
        ),
        accessor: 'created_at',
        width: 180,
        allowShrinking: false,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return row.original.created_at ? formatDateTime(row.original.created_at) : '';
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'journey_name'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='journey_name'
            onClick={onTableHeaderClick}
            title='Journey Name'
            tooltip='Journey Name'
          />
        ),
        accessor: 'journey_name',
        width: 200,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return row.original.journey.name;
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={false}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='journey_url'
            onClick={()=>{}}
            title='Journey URL'
            tooltip='Journey URL'
          />
        ),
        accessor: 'journey_url',
        width: 180,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return row.original.journey_url;
        },
      }),
    
    ],
    [sort_by, sort_order, onTableHeaderClick]
  );

  const onPageChange = useCallback((e: any, newPage: number) => {
    updatePage(newPage);
  }, []);

  const onRowClick = useCallback(
    (row: any) => {
      const session = row.original;
      
      // Check if journey is accessible (only when journeys are loaded)
      if (journeys.length > 0 && !isJourneyAccessible(session.journey, journeys, subscriptionPlanType, isOnTrial)) {
        showUpgradeModal(
          'unlimited_journeys',
          'You have reached your journey limit on the Start plan. Upgrade to Grow plan to view session replay for this journey and access all your journeys.',
          'Upgrade to Access All Journeys'
        );
        return;
      }
      
      history.push(`/${currentOrganization.slug}/sessions/${session?.session_uuid}`);
    },
    [currentOrganization.slug, history, journeys, subscriptionPlanType, isOnTrial, showUpgradeModal]
  );

  const renderContent = () => {
    if (loading || isNull(loading)) {
      return <FixedLoadingSpinner />;
    }

    if (sessions && sessions.length > 0) {
      return <DataTable columns={columns} data={sessions} onRowClick={onRowClick} />;
    }

    if (search_term) {
      return <div className='text-bedrock-p text-bedrock-black'>No session replays found</div>;
    }

    return (
      <div className='max-w-[640px] text-bedrock-p text-bedrock-black'>
        Once your journeys are viewed, you will see their session replays here.
      </div>
    );
  };

  return (
    <div className='flex flex-col'>
      <Helmet>
        <title>Journey | Sessions</title>
      </Helmet>
      {renderContent()}
      {/* {pages > 1 && (
        <div className='mt-6'>
          <Pagination count={pages} page={page} onChange={onPageChange} />
        </div>
      )} */}
    </div>
  );
};
